import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, Music, Video, LayoutGrid } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { SortableColumn, useTableSort } from '@/components/ui/SortableColumn';
import { api } from '@/lib/api';
import { cn, formatDate, formatNumber } from '@/lib/utils';

// Aba Conteúdo unificada em 2026-07-08 — antes tinha 'Conteúdo' e
// 'Reproduções' separadas no menu. Consolidado em uma pagina só com
// sub-abas Tudo/Musicas/Clipes. Metricas de reproducao (plays, likes,
// views) agora ficam como colunas no lugar de aba separada.

type Tab = 'all' | 'track' | 'clip';

const TABS: { id: Tab; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'all',   label: 'Tudo',    icon: LayoutGrid },
  { id: 'track', label: 'Músicas', icon: Music },
  { id: 'clip',  label: 'Clipes',  icon: Video },
];

export function Content() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-content', tab, debouncedSearch],
    queryFn: async () => {
      const params: any = { limit: tab === 'all' ? 120 : 100 };
      if (debouncedSearch) params.search = debouncedSearch;
      const endpoint = tab === 'all' ? '/admin/content/all' : `/admin/content/${tab}`;
      const { data } = await api.get(endpoint, { params });
      return Array.isArray(data?.data) ? data.data : [];
    },
  });

  const remove = useMutation({
    mutationFn: ({ id, type }: { id: string; type: 'track' | 'clip' }) =>
      api.delete(`/admin/content/${type}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-content'] }),
  });

  const rawItems: any[] = data ?? [];
  const { sorted: items, sort, handleSort } = useTableSort(rawItems, 'created_at', 'desc', (row, field) => {
    if (field === 'author') return row.display_name || row.username;
    if (field === 'engagement') {
      // Métrica única pra ordenar tanto tracks quanto clips ao mesmo tempo.
      return (row.plays_count || 0) + (row.views_count || 0);
    }
    return row[field];
  });

  return (
    <Layout title="Conteúdo">
      <div className="mb-4 flex flex-wrap items-center gap-1 rounded-lg border border-border bg-bg-elevated p-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
              tab === id
                ? 'bg-brand-bg text-brand'
                : 'text-text-secondary hover:bg-bg-input hover:text-text-primary'
            )}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Buscar por título ou autor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-bg-input text-left text-xs uppercase text-text-secondary">
            <tr>
              {tab === 'all' && (
                <SortableColumn label="Tipo" field="type" sort={sort} onSort={handleSort} />
              )}
              <SortableColumn label="Título" field="title" sort={sort} onSort={handleSort} />
              <SortableColumn label="Autor" field="author" sort={sort} onSort={handleSort} />
              <SortableColumn label="Status" field="status" sort={sort} onSort={handleSort} />
              {tab !== 'clip' && (
                <SortableColumn label="Plays" field="plays_count" sort={sort} onSort={handleSort} align="right" />
              )}
              {tab !== 'track' && (
                <SortableColumn label="Views" field="views_count" sort={sort} onSort={handleSort} align="right" />
              )}
              <SortableColumn label="Curtidas" field="likes_count" sort={sort} onSort={handleSort} align="right" />
              <SortableColumn label="Criado em" field="created_at" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="p-8 text-center text-text-muted">Carregando…</td></tr>
            )}
            {items.map((c) => (
              <tr key={`${c.type || tab}-${c.id}`} className="border-b border-border last:border-0">
                {tab === 'all' && (
                  <td className="px-4 py-3">
                    {c.type === 'track' ? (
                      <span className="inline-flex items-center gap-1 rounded bg-brand-bg px-1.5 py-0.5 text-[10px] font-medium uppercase text-brand">
                        <Music size={10} />
                        Música
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-purple-950/60 px-1.5 py-0.5 text-[10px] font-medium uppercase text-purple-300">
                        <Video size={10} />
                        Clipe
                      </span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 font-medium text-text-primary">{c.title || '(sem título)'}</td>
                <td className="px-4 py-3">
                  <div className="text-text-secondary">{c.display_name || c.username}</div>
                  <div className="text-xs text-text-muted">@{c.username}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-md px-2 py-0.5 text-xs ${c.status === 'published' ? 'bg-emerald-950 text-emerald-300' : 'bg-amber-950 text-amber-300'}`}>
                    {c.status}
                  </span>
                </td>
                {tab !== 'clip' && (
                  <td className="px-4 py-3 text-right font-semibold text-brand">
                    {c.type !== 'clip' ? formatNumber(c.plays_count || 0) : <span className="text-text-muted">—</span>}
                  </td>
                )}
                {tab !== 'track' && (
                  <td className="px-4 py-3 text-right font-semibold text-purple-300">
                    {c.type !== 'track' ? formatNumber(c.views_count || 0) : <span className="text-text-muted">—</span>}
                  </td>
                )}
                <td className="px-4 py-3 text-right text-text-secondary">{formatNumber(c.likes_count || 0)}</td>
                <td className="px-4 py-3 text-text-secondary">{formatDate(c.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Remover conteúdo"
                    onClick={() => {
                      const kind = (c.type || tab) === 'track' ? 'a música' : 'o clipe';
                      if (confirm(`Remover ${kind} "${c.title || c.id}"? Esta ação não pode ser desfeita.`)) {
                        remove.mutate({ id: c.id, type: (c.type || tab) as 'track' | 'clip' });
                      }
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-text-muted">Nenhum conteúdo encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
